/* zedaro-quick-search.js — instant parts lookup for the home page.
 *
 * Deliberately NOT built on InstantSearch. The search PAGE needs the adapter
 * because it drives faceting, refinement state and URL sync; a dropdown that
 * shows ten rows needs one debounced multi_search and nothing else. Loading a
 * second copy of the adapter on the home page would cost every visitor a
 * bundle to render a list.
 *
 * ACCESS CONTROL IS THE SAME MECHANISM AS THE SEARCH PAGE and must stay that
 * way: logged-out visitors search with the public-only key, logged-in
 * customers fetch a per-customer scoped key from the zOps App Proxy whose
 * private_* filter is baked in server-side and cannot be edited by the
 * browser. On ANY failure the public key is kept — never an unfiltered one —
 * so a proxy outage degrades to "public products only", never to a leak.
 */
(function () {
  "use strict";

  function init(root) {
    if (root.getAttribute("data-zd-qs-inited")) return;   /* one init per bar, even if the script loads twice */
    root.setAttribute("data-zd-qs-inited", "1");

  var cfg = {
    host:       root.dataset.host || "",
    port:       root.dataset.port || "443",
    protocol:   root.dataset.protocol || "https",
    key:        root.dataset.key || "",
    proxy:      root.dataset.proxy || "",
    collection: root.dataset.collection || "products",
    currency:   root.dataset.currency || "CAD",
    loggedIn:   root.dataset.loggedIn === "true",
    approved:   root.dataset.approved === "true",   /* only approved accounts see prices / can add */
    searchUrl:  root.dataset.searchUrl || "/pages/search",
    perPage:    parseInt(root.dataset.perPage, 10) || 8,
    keyTtl:     300
  };
  // No early bail on a missing host/key: the proxy supplies both. Section
  // settings only matter if the proxy cannot be reached.


  var money = new Intl.NumberFormat(undefined, { style: "currency", currency: cfg.currency });
  var input = root.querySelector(".zd-qs__input");
  var panel = root.querySelector(".zd-qs__panel");
  var list  = root.querySelector(".zd-qs__list");
  var foot  = root.querySelector(".zd-qs__foot");
  var empty = root.querySelector(".zd-qs__empty");
  var busy  = root.querySelector(".zd-qs__busy");
  if (!input || !panel || !list) return;

  /* Identifier normalisation — mirrors zd_search_facets.py and the search
   * page's copy. Part numbers are typed with and without dashes, spaces and
   * leading zeros; the index carries pre-normalised ids_* fields so an exact
   * part-number entry beats fuzzy text every time. */
  function normalizeExact(raw) {
    return String(raw).replace(/[\s\-]+/g, "").toLowerCase();
  }
  function zeroLooseKey(raw) {
    var norm = normalizeExact(raw);
    return norm.replace(/^0+/, "").replace(/0+$/, "");
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  var abort = null;
  var timer = null;
  var lastQuery = "";

  function search(q) {
    if (!cfg.host || !cfg.key) {
      return Promise.reject(new Error("search not configured"));
    }
    if (abort) abort.abort();
    abort = new AbortController();

    /* Four tiers in ONE multi_search round trip, first non-empty wins:
     * exact identifier, zero-loose identifier, free text, then vendor.
     * Someone who types a full part number gets that part, not sixty things
     * whose descriptions happen to share a token.
     *
     * query_by MUST NAME FIELDS THE COLLECTION ACTUALLY HAS. Typesense fails
     * the whole search when it does not, and a failed search returns no hits —
     * which this dropdown then reported as "no parts match", i.e. a schema
     * error wearing the costume of an empty result. The first three tiers use
     * exactly what the search page uses (title, ids_exact, ids_loose), which
     * is proven against the live collection.
     *
     * The vendor tier is LAST and deliberately separate: `vendor` is in the
     * indexed document but is not part of the search page's query_by, so
     * whether it is a queryable string field is not established here. As its
     * own search it costs nothing to be wrong — if the field is not
     * searchable that one tier errors, returns no hits, and the tiers above
     * have already answered. If it is searchable, typing a vendor name finds
     * that vendor's parts, which is what a person typing "Flair" expects. */
    var searches = [
      { collection: cfg.collection, q: "*",
        filter_by: "ids_exact:=" + JSON.stringify(normalizeExact(q)),
        per_page: cfg.perPage },
      { collection: cfg.collection, q: "*",
        filter_by: "ids_loose:=" + JSON.stringify(zeroLooseKey(q)),
        per_page: cfg.perPage },
      /* max_candidates: the default of 4 prefix expansions is far too few for
         part-number prefixes on this catalogue and silently hides whole
         families of parts — see the long note in zedaro-search.js. The two
         filter_by lookups above are exact-key matches and need no expansion. */
      { collection: cfg.collection, q: q, query_by: "title,ids_exact,ids_loose",
        max_candidates: 1000, per_page: cfg.perPage },
      { collection: cfg.collection, q: q, query_by: "vendor",
        max_candidates: 1000, per_page: cfg.perPage }
    ];

    return fetch(cfg.protocol + "://" + cfg.host + ":" + cfg.port + "/multi_search", {
      method: "POST",
      signal: abort.signal,
      headers: { "Content-Type": "application/json", "X-TYPESENSE-API-KEY": cfg.key },
      body: JSON.stringify({ searches: searches })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var results = data.results || [];
        for (var i = 0; i < results.length; i++) {
          if (results[i].hits && results[i].hits.length) return results[i].hits;
        }
        /* Every tier empty AND every tier errored means the search could not
         * run — a schema or key problem, not an absent part. Saying so is the
         * difference between a five-minute fix and a hunt for a product that
         * was there all along. The tier errors are logged, never shown: they
         * name internal fields. */
        var errored = 0;
        for (var j = 0; j < results.length; j++) {
          if (results[j].error) { errored++; console.warn("[zd-qs] tier error:", results[j].error); }
        }
        if (results.length && errored === results.length) throw new Error("all tiers failed");
        return [];
      });
  }

  function rowHtml(hit) {
    var d = hit.document || {};
    // Same reasoning as addToCart: the handle is already url-safe, and
    // encoding it produces a 404 on a product that exists.
    var href = "/products/" + String(d.handle || "").replace(/^\/+/, "");
    return '' +
      '<li class="zd-qs__row" data-handle="' + escapeHtml(d.handle) + '">' +
        '<a class="zd-qs__link" href="' + href + '">' +
          (d.image
            ? '<img class="zd-qs__thumb" loading="lazy" src="' + escapeHtml(d.image) + '" alt="">'
            : '<span class="zd-qs__thumb zd-qs__thumb--none" aria-hidden="true"></span>') +
          '<span class="zd-qs__text">' +
            '<span class="zd-qs__title">' + escapeHtml(d.title) + '</span>' +
            '<span class="zd-qs__meta">' +
              escapeHtml(d.vendor || "") +
              (d.id ? ' &middot; ZD# ' + escapeHtml(d.id) : "") +
              (cfg.approved && typeof d.price_min === "number"
                ? ' &middot; ' + money.format(d.price_min)
                : (cfg.approved && typeof d.price === "number" ? ' &middot; ' + money.format(d.price) : "")) +
            '</span>' +
          '</span>' +
        '</a>' +
        (cfg.approved
          ? '<button type="button" class="zd-qs__add" data-handle="' + escapeHtml(d.handle) + '">Add</button>'
          : "") +
      '</li>';
  }

  function render(hits, q) {
    if (!hits.length) {
      list.innerHTML = "";
      if (empty) {
        empty.textContent = 'No parts match "' + q + '".';
        empty.hidden = false;
      }
    } else {
      if (empty) empty.hidden = true;
      list.innerHTML = hits.map(rowHtml).join("");
    }
    if (foot) {
      foot.innerHTML = '<a class="zd-qs__advanced" href="' + cfg.searchUrl +
        "?q=" + encodeURIComponent(q) + '">Advanced search for &ldquo;' +
        escapeHtml(q) + '&rdquo; &rarr;</a>';
      foot.hidden = false;
    }
    open();
  }

  function open()  { panel.hidden = false; root.classList.add("zd-qs--open"); }
  function close() { panel.hidden = true;  root.classList.remove("zd-qs--open"); }

  function run() {
    var q = input.value.trim();
    if (q.length < 2) { close(); lastQuery = ""; return; }
    if (q === lastQuery) { open(); return; }
    lastQuery = q;
    if (busy) busy.hidden = false;
    /* Renew first if the key is past three quarters of its life — cheap, and
       it means a search never fails for the one reason that is entirely
       predictable. */
    ready
      .then(function () { return keyIsStale() ? refreshKey() : null; })
      .then(function () { return search(q); })
      .then(function (hits) {
        if (busy) busy.hidden = true;
        render(hits, q);
      })
      .catch(function (err) {
        if (busy) busy.hidden = true;
        if (err && err.name === "AbortError") return;
        /* A search that cannot run says so and offers the full page, rather
         * than showing an empty dropdown that reads as "no such part". */
        console.warn("[zd-qs] search failed:", err && err.message ? err.message : err);
        list.innerHTML = "";
        if (empty) {
          empty.textContent = "Search is unavailable right now — try the full search page.";
          empty.hidden = false;
        }
        open();
      });
  }

  /* ── Add to cart ──────────────────────────────────────────────────────────
   * The Typesense document carries no variant id (it indexes products, one
   * document per product), so the variant is resolved on click from Shopify's
   * own product JSON. One fetch, only when somebody actually adds something —
   * cheaper than indexing variant ids for 95,000 products to serve the small
   * fraction that get added from a dropdown.
   *
   * Quantity is 1. Minimum order quantities live in the zedaro.min_qty
   * metafield, which product JSON does not expose; rather than guess, the cart
   * banner (zedaro-price-breaks.js) already names any line below its minimum
   * and the tier checkout refuses it server-side with `below_minimum`. If
   * quick-add ever needs to be minimum-aware at the point of click, the honest
   * fix is to index min_qty alongside the product — a zd_search_reindex.py
   * change plus a reindex — not a client-side guess.
   */
  function updateCartCount() {
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var targets = document.querySelectorAll(
          ".header__cart-count," +                  /* Warehouse */
          ".cart-count-bubble span[aria-hidden]," + /* Dawn / Trade */
          "[data-cart-count]");
        for (var i = 0; i < targets.length; i++) {
          targets[i].textContent = cart.item_count;
        }
      })
      .catch(function () { /* a stale badge is not worth an error */ });
  }

  function addToCart(handle, button) {
    var original = button.textContent;
    button.disabled = true;
    button.textContent = "Adding…";

    /* encodeURIComponent is deliberately NOT used on the handle (2026-08-11).
       Shopify handles are already url-safe (lowercase, digits, hyphens), and
       encoding turns any stray character into a %XX that Shopify does not
       resolve — producing a 404 on a product that exists. The failure showed
       up as Add falling back to "Open" and that link landing on Page Not
       Found, i.e. the same bad url twice. */
    var productPath = "/products/" + String(handle || "").replace(/^\/+/, "");

    fetch(productPath + ".js", { credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw new Error("product json " + r.status + " for " + productPath);
        return r.json();
      })
      .then(function (product) {
        if (!product || !product.variants || !product.variants.length) {
          throw new Error("no variants on " + productPath);
        }
        var variant = null;
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].available) { variant = product.variants[i]; break; }
        }
        if (!variant) variant = product.variants[0];
        return fetch("/cart/add.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
        });
      })
      .then(function (resp) {
        if (!resp || !resp.ok) {
          return resp.json().catch(function () { return {}; }).then(function (j) {
            throw new Error("cart refused (" + (resp ? resp.status : "?") + "): " +
                            (j.description || j.message || "no detail"));
          });
        }
        button.textContent = "Added \u2713";
        /* THE ADD SUCCEEDS; ONLY THE COUNTER WAS THE PROBLEM (2026-08-11).
           This used to dispatch a generic cart:refresh event and call
           Shopify.onCartUpdate, on the theory that letting the theme update
           itself avoids having to know its internals. Warehouse's
           theme.min.js listens, then throws "Cannot set properties of null
           (setting 'textContent')" because the element it expects is not on
           the home page — an exception inside someone else's minified code,
           raised by ours, which we cannot fix there.
           The counter is updated directly instead, across the selectors both
           themes actually use. Anything absent is skipped, and no event is
           published, so no third-party handler can fail on our account. */
        updateCartCount();
        setTimeout(function () {
          button.textContent = original;
          button.disabled = false;
        }, 1800);
      })
      .catch(function (err) {
        /* Say why, in the console. This failure was diagnosed twice by
           guesswork; the reason is always known at this point. */
        console.warn("[zd-qs] add to cart failed:", err && err.message ? err.message : err);
        /* Never leave a dead button. Sending someone to the product page is a
         * real answer: approval gates, minimums and variant choices all
         * resolve there. */
        button.textContent = "Open \u2192";
        button.disabled = false;
        button.onclick = function () { window.location.href = productPath; };
      });
  }

  /* ── Wiring ──────────────────────────────────────────────────────────── */

  input.addEventListener("input", function () {
    clearTimeout(timer);
    timer = setTimeout(run, 180);
  });

  input.addEventListener("keydown", function (e) {
    if (e.key === "Enter") {
      e.preventDefault();
      var q = input.value.trim();
      if (q) window.location.href = cfg.searchUrl + "?q=" + encodeURIComponent(q);
    } else if (e.key === "Escape") {
      close();
      input.blur();
    }
  });

  input.addEventListener("focus", function () {
    if (input.value.trim().length >= 2 && list.innerHTML) open();
  });

  list.addEventListener("click", function (e) {
    var btn = e.target.closest(".zd-qs__add");
    if (!btn) return;
    e.preventDefault();
    addToCart(btn.getAttribute("data-handle"), btn);
  });

  document.addEventListener("click", function (e) {
    if (!root.contains(e.target)) close();
  });

  /* Scoped key, exactly as the search page does it — INCLUDING ITS EXPIRY.
   *
   * A customer scoped key carries expires_at and the proxy issues it with a
   * short TTL. Fetching it once at page load means every search after that
   * moment 401s (2026-08-11), which this dropdown would report as "no parts
   * match" — a dead search wearing the costume of an empty catalogue. The
   * search page had the same defect and the same fix.
   *
   * Two refresh triggers, because a home page is a different animal from a
   * search page: a timer at 75% of the TTL covers a tab left open, and a
   * staleness check before each search covers a laptop that was asleep
   * (timers do not fire while suspended, so the timer alone is not enough).
   * A failed refresh keeps the current key rather than falling back to the
   * public one — a signed-in customer never silently loses sight of their
   * own products. */
  var keyFetchedAt = 0;
  /* Nothing searches before the first proxy answer lands (2026-08-11). The
     dropdown fires on the second keystroke, which on a home page is well
     inside the round trip — so without this the first search went out with
     whatever the section settings held (a key that may be stale, or none at
     all now that the proxy is the source of truth) and came back 401 or empty.
     The search PAGE never showed this because it does not start InstantSearch
     until the same fetch settles; the dropdown had no equivalent gate. */
  var ready = Promise.resolve();

  /* EVERY visitor fetches, not just signed-in ones (2026-08-11). The proxy
   * returns a public-only key for a logged-out browser and a scoped one for a
   * customer, both minted live from the current parent — so the theme holds no
   * key of its own to go stale. It also returns host, port, protocol and
   * collection, which means the section settings are a FALLBACK for a proxy
   * outage rather than a second copy of the truth that somebody has to keep
   * in step across two sections and two themes. */
  function refreshKey() {
    if (!cfg.proxy) return Promise.resolve();
    return fetch(cfg.proxy, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        if (data.key) { cfg.key = data.key; keyFetchedAt = Date.now(); }
        if (data.ttl) cfg.keyTtl = Number(data.ttl) || cfg.keyTtl;
        if (data.host) cfg.host = data.host;
        if (data.port) cfg.port = String(data.port);
        if (data.protocol) cfg.protocol = data.protocol;
        if (data.collection) cfg.collection = data.collection;
      })
      .catch(function () { /* keep whatever we have — never a wider key */ });
  }

  function keyIsStale() {
    if (!cfg.proxy || !keyFetchedAt) return false;
    var ttl = (cfg.keyTtl || 300) * 1000;
    return Date.now() - keyFetchedAt > ttl * 0.75;
  }

  if (cfg.proxy) {
    input.setAttribute("data-loading", "1");
    ready = refreshKey().then(function () {
      input.removeAttribute("data-loading");
      var everyMs = Math.max(30, Math.floor((cfg.keyTtl || 300) * 0.75)) * 1000;
      setInterval(refreshKey, everyMs);
    });
  }
  }  /* end init(root) */

  /* Initialise every quick-search bar on the page. There can be more than one
   * now — the always-on header bar plus, on the home page, the hero section. */
  var _zdqsRoots = document.querySelectorAll(".zd-qs");
  for (var _zi = 0; _zi < _zdqsRoots.length; _zi++) init(_zdqsRoots[_zi]);
})();
